"use client"
import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { loadStripe } from '@stripe/stripe-js'
import { httpsCallable } from 'firebase/functions'
import { functions } from '../../lib/firebase'
import { useSubscription } from '../../contexts/subscription-context'
import { Check, Sparkles, CheckCircle } from 'lucide-react'
import { getConfigValues } from '../../lib/remote-config'
import { SubscriptionService } from '../../services/subscription-service'

const getStripePromise = async () => {
  const { stripePublicKey } = getConfigValues();
  return loadStripe(stripePublicKey);
};

export default function Plans() {
  const navigate = useNavigate()
  const { remainingChats, subscription, hasActiveSubscription } = useSubscription()
  const [loading, setLoading] = useState({ pro: false, ultimate: false })
  const [error, setError] = useState('')
  const [monthlyFreeDebates, setMonthlyFreeDebates] = useState('--')
  const [proPlanPrice, setProPlanPrice] = useState('30')
  const [ultimatePlanPrice, setUltimatePlanPrice] = useState('100')

  useEffect(() => {
    const { monthlyFreeDebates, proPlanMonthlyPrice, ultimatePlanMonthlyPrice } = getConfigValues();
    setMonthlyFreeDebates(monthlyFreeDebates);
    
    // Set the pro plan price from remote config, with fallback
    if (proPlanMonthlyPrice && !isNaN(Number(proPlanMonthlyPrice))) {
      setProPlanPrice(proPlanMonthlyPrice);
    }
    
    // Set the ultimate plan price from remote config, with fallback
    if (ultimatePlanMonthlyPrice && !isNaN(Number(ultimatePlanMonthlyPrice))) {
      setUltimatePlanPrice(ultimatePlanMonthlyPrice);
    }
  }, []);

  // Helper functions to check subscription status
  const isFreePlan = () => !hasActiveSubscription();
  const isProPlan = () => hasActiveSubscription() && subscription?.tier === 'pro';
  const isUltimatePlan = () => hasActiveSubscription() && subscription?.tier === 'ultimate';

  const handleSubscribe = async (tier) => {
    try {
      setLoading({ ...loading, [tier]: true })
      setError('')

      // If already on this plan, navigate to settings
      if ((tier === 'pro' && isProPlan()) || (tier === 'ultimate' && isUltimatePlan())) {
        navigate('/settings');
        return;
      }

      // If upgrading from Pro to Ultimate, redirect to subscription management portal
      if (tier === 'ultimate' && isProPlan()) {
        await SubscriptionService.manageSubscription();
        return;
      }

      const createStripeCheckout = httpsCallable(functions, 'createCheckoutSession')
      const { data } = await createStripeCheckout({ tier })
      
      if (!data?.sessionId) {
        throw new Error('Failed to create checkout session')
      }

      const stripe = await getStripePromise()
      if (!stripe) {
        throw new Error('Stripe failed to load')
      }

      const { error } = await stripe.redirectToCheckout({
        sessionId: data.sessionId,
      })

      if (error) {
        throw error
      }
    } catch (err) {
      console.error('Checkout error:', err)
      setError(err.message || 'Failed to start checkout. Please try again.')
    } finally {
      setLoading({ pro: false, ultimate: false })
    }
  }

  // Helper function to get the button text based on subscription status
  const getButtonText = (planType) => {
    if (planType === 'free' && isFreePlan()) {
      return 'Your current plan';
    }
    
    if (planType === 'pro' && isProPlan()) {
      return 'Your current plan';
    }
    
    if (planType === 'ultimate' && isUltimatePlan()) {
      return 'Your current plan';
    }

    if (planType === 'free') {
      return 'Continue with Free';
    }

    if (planType === 'pro') {
      return isUltimatePlan() ? 'Downgrade to Pro' : 'Upgrade to Pro';
    }
    
    if (planType === 'ultimate') {
      return 'Get Ultimate Access';
    }
    
    return 'Select Plan';
  };

  // Helper function to determine if a plan should be shown
  const shouldShowPlan = (planType) => {
    if (planType === 'free') {
      return isFreePlan();
    }
    
    if (planType === 'pro') {
      return isFreePlan() || isProPlan();
    }
    
    if (planType === 'ultimate') {
      return isFreePlan() || isProPlan() || isUltimatePlan();
    }
    
    return true;
  };

  const features = [
    {
      name: 'Monthly debates with GPT-4o mini',
      free: `${monthlyFreeDebates} debates per month`,
      pro: 'Unlimited debates',
      ultimate: 'Unlimited debates'
    },
    {
      name: 'Access to GPT-4o',
      free: 'Not available',
      pro: '20 debates per month',
      ultimate: 'Unlimited debates'
    },
    {
      name: 'Access to Claude 3.5 Sonnet',
      free: 'Not available',
      pro: '20 debates per month',
      ultimate: 'Unlimited debates'
    },
    {
      name: 'Access to Gemini Flash Lite',
      free: 'Not available',
      pro: 'Unlimited debates',
      ultimate: 'Unlimited debates'
    },
    {
      name: 'Access to GPT 4.5',
      free: 'Not available',
      pro: 'Not available',
      ultimate: 'Unlimited debates'
    },
    {
      name: 'Access to Claude 3.7 Sonnet',
      free: 'Not available',
      pro: 'Not available',
      ultimate: 'Unlimited debates'
    },
    {
      name: 'Access to Gemini Flash & Pro Exp',
      free: 'Not available',
      pro: 'Not available',
      ultimate: 'Unlimited debates'
    },
    {
      name: 'Follow-up questions',
      free: 'Basic debate only',
      pro: 'Unlimited follow-ups',
      ultimate: 'Unlimited follow-ups'
    },
    {
      name: 'In-depth analysis',
      free: 'Basic debate format',
      pro: 'Drill down into specific points',
      ultimate: 'Full exploration with best models'
    },
    {
      name: 'Debate flexibility',
      free: 'Single topic per debate',
      pro: 'Branch into related topics',
      ultimate: 'Maximum depth and complexity'
    }
  ]

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 flex flex-col">
      <header className="fixed top-0 w-full bg-black/50 backdrop-blur-md border-b border-gray-800">
        <nav className="container mx-auto px-4 py-4">
          <button
            onClick={() => navigate('/chat')}
            className="text-2xl font-bold bg-gradient-to-r from-purple-400 to-pink-600 text-transparent bg-clip-text"
          >
            Verdyct
          </button>
        </nav>
      </header>

      <div className="flex-1 flex items-center justify-center px-4 pt-20 pb-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-6xl"
        >
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-purple-400 via-pink-500 to-red-500 text-transparent bg-clip-text">
              Elevate Your Intellectual Discourse
            </h1>
            <p className="text-xl text-gray-400 mb-4">
              {isFreePlan() ? 
                remainingChats > 0
                  ? `You have ${remainingChats} free debates remaining this month`
                  : 'You have used all your free debates for this month'
                : `You are currently on the ${isUltimatePlan() ? 'Ultimate' : 'Pro'} plan`}
            </p>
            {!isUltimatePlan() && (<p className="text-gray-300 max-w-2xl mx-auto">
              Choose the plan that fits your intellectual curiosity and dive deeper into debates with more advanced AI models.
            </p>)}
          </div>

          <div className={`grid gap-6 mx-auto ${
            // Adjust grid and max-width based on number of visible plans
            isFreePlan() 
              ? 'md:grid-cols-3 max-w-6xl' // All 3 plans
              : isProPlan()
              ? 'md:grid-cols-2 max-w-[800px]' // Pro and Ultimate
              : 'md:grid-cols-1 max-w-[400px]' // Ultimate only
          }`}>
            {/* Each plan card should have a fixed width */}
            {shouldShowPlan('free') && (
              <div className="bg-gray-900/50 backdrop-blur-sm rounded-2xl border border-gray-800 p-6 lg:p-8 h-full flex flex-col relative w-full">
                {isFreePlan() && (
                  <div className="absolute top-4 right-4">
                    <div className="flex items-center justify-center w-6 h-6 rounded-full bg-white text-gray-900">
                      <CheckCircle className="w-5 h-5 text-gray-900" />
                    </div>
                  </div>
                )}

                <div className="h-8 flex items-start">
                  {/* Empty div to match badge height */}
                </div>
                <h2 className="text-2xl font-bold mb-2">Free Plan</h2>
                <p className="text-gray-400 mb-6 h-12 text-sm">Perfect for trying out AI-powered debates</p>
                <div className="text-2xl lg:text-3xl font-bold mb-6">
                  {monthlyFreeDebates} <span className="text-gray-400 text-lg font-normal">debates/month</span>
                </div>
                <ul className="space-y-3 mb-8 flex-1 text-sm">
                  {features.map((feature) => (
                    <li key={feature.name} className="flex items-start text-gray-300 min-h-[3rem]">
                      <Check className="w-5 h-5 text-purple-500 mr-2 mt-0.5 flex-shrink-0" />
                      <div>
                        <span className="font-medium">{feature.name}</span>
                        <p className="text-xs text-gray-400">{feature.free}</p>
                      </div>
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => navigate('/chat')}
                  disabled={isFreePlan()}
                  className={`w-full py-3 px-4 rounded-lg font-semibold transition-colors h-[46px] ${
                    isFreePlan() 
                      ? 'bg-gray-600 text-white cursor-not-allowed opacity-80' 
                      : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                  }`}
                >
                  {getButtonText('free')}
                </button>
              </div>
            )}

            {shouldShowPlan('pro') && (
              <div className="bg-gradient-to-br from-purple-900/50 to-pink-900/50 backdrop-blur-sm rounded-2xl border border-purple-500/20 p-6 lg:p-8 relative overflow-hidden h-full flex flex-col w-full">
                {isProPlan() && (
                  <div className="absolute top-4 right-4">
                    <div className="flex items-center justify-center w-6 h-6 rounded-full bg-white text-gray-900">
                      <CheckCircle className="w-5 h-5 text-gray-900" />
                    </div>
                  </div>
                )}

                <div className="absolute inset-0 bg-gradient-to-br from-purple-500/10 to-pink-500/10" />
                <div className="relative flex flex-col h-full">
                  <div className="h-8 flex items-start">
                    <div className="inline-block px-3 py-1 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full text-sm font-semibold">
                      POPULAR
                    </div>
                  </div>
                  <h2 className="text-2xl font-bold mb-2">Pro Plan</h2>
                  <p className="text-gray-300 mb-6 h-12 text-sm">For deeper intellectual exploration</p>
                  <div className="text-2xl lg:text-3xl font-bold mb-6">
                    ${proPlanPrice} <span className="text-gray-300 text-lg font-normal">/month</span>
                  </div>
                  <ul className="space-y-3 mb-8 flex-1 text-sm">
                    {features.map((feature) => (
                      <li key={feature.name} className="flex items-start text-gray-200 min-h-[3rem]">
                        <Check className="w-5 h-5 text-purple-400 mr-2 mt-0.5 flex-shrink-0" />
                        <div>
                          <span className="font-medium">{feature.name}</span>
                          <p className="text-xs text-purple-300">{feature.pro}</p>
                        </div>
                      </li>
                    ))}
                  </ul>
                  {error && (
                    <div className="mb-4 p-3 rounded bg-red-500/10 border border-red-500/50 text-red-500 text-sm">
                      {error}
                    </div>
                  )}
                  <button
                    onClick={() => handleSubscribe('pro')}
                    disabled={loading.pro || loading.ultimate || isProPlan()}
                    className={`w-full py-3 px-4 text-white rounded-lg font-semibold transition-opacity h-[46px] ${
                      isProPlan() 
                        ? 'bg-gradient-to-r from-purple-500 to-pink-600 opacity-80 cursor-not-allowed' 
                        : 'bg-gradient-to-r from-purple-500 to-pink-600 hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed'
                    }`}
                  >
                    {loading.pro ? (
                      <div className="flex items-center justify-center">
                        <div className="w-5 h-5 border-t-2 border-b-2 border-white rounded-full animate-spin mr-2"></div>
                        Processing...
                      </div>
                    ) : (
                      getButtonText('pro')
                    )}
                  </button>
                </div>
              </div>
            )}

            {shouldShowPlan('ultimate') && (
              <div className="bg-gradient-to-br from-blue-900/50 via-violet-900/50 to-indigo-900/50 backdrop-blur-sm rounded-2xl border border-indigo-500/30 p-6 lg:p-8 relative overflow-hidden h-full flex flex-col w-full">
                {isUltimatePlan() && (
                  <div className="absolute top-4 right-4">
                    <div className="flex items-center justify-center w-6 h-6 rounded-full bg-white text-gray-900">
                      <CheckCircle className="w-5 h-5 text-gray-900" />
                    </div>
                  </div>
                )}

                <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 via-violet-500/10 to-indigo-500/10" />
                <div className="relative flex flex-col h-full">
                  <div className="h-8 flex items-start">
                    <div className="inline-block px-3 py-1 bg-gradient-to-r from-blue-500 via-violet-500 to-indigo-500 rounded-full text-sm font-semibold flex items-center">
                      <Sparkles className="w-4 h-4 mr-1 inline" />
                      <span>ULTIMATE</span>
                    </div>
                  </div>
                  <h2 className="text-2xl font-bold mb-2">Ultimate Plan</h2>
                  <p className="text-gray-300 mb-6 h-12 text-sm">For unlimited access to the most advanced AI models</p>
                  <div className="text-2xl lg:text-3xl font-bold mb-6">
                    ${ultimatePlanPrice} <span className="text-gray-300 text-lg font-normal">/month</span>
                  </div>
                  <ul className="space-y-3 mb-8 flex-1 text-sm">
                    {features.map((feature) => (
                      <li key={feature.name} className="flex items-start text-gray-200 min-h-[3rem]">
                        <Check className="w-5 h-5 text-indigo-400 mr-2 mt-0.5 flex-shrink-0" />
                        <div>
                          <span className="font-medium">{feature.name}</span>
                          <p className="text-xs text-indigo-300">{feature.ultimate}</p>
                        </div>
                      </li>
                    ))}
                  </ul>
                  {error && (
                    <div className="mb-4 p-3 rounded bg-red-500/10 border border-red-500/50 text-red-500 text-sm">
                      {error}
                    </div>
                  )}
                  <button
                    onClick={() => handleSubscribe('ultimate')}
                    disabled={loading.pro || loading.ultimate || isUltimatePlan()}
                    className={`w-full py-3 px-4 text-white rounded-lg font-semibold transition-opacity h-[46px] ${
                      isUltimatePlan() 
                        ? 'bg-gradient-to-r from-blue-500 via-violet-500 to-indigo-500 opacity-80 cursor-not-allowed' 
                        : 'bg-gradient-to-r from-blue-500 via-violet-500 to-indigo-500 hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed'
                    }`}
                  >
                    {loading.ultimate ? (
                      <div className="flex items-center justify-center">
                        <div className="w-5 h-5 border-t-2 border-b-2 border-white rounded-full animate-spin mr-2"></div>
                        Processing...
                      </div>
                    ) : (
                      getButtonText('ultimate')
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  )
}

